<template>
    <div>
        <Loader v-if="loading" />
        <div v-else class="container-fluid mt-45">
            <div class="row">
                <div class="col-md-6">
                    <Breadcrumbs :hideBackButton="true" />
                </div>

                <div class="col-md-6 d-flex align-items-center justify-content-end">
                    <div v-if="tokenExpirySeconds != null && tokenExpirySeconds > 0">
                        <p class="font-weight-bold mb-0">Time remaining {{ tokenExpirySeconds | moment('mm:ss') }} <img id="tooltip-target" class="ml-1" src="@/assets/images/icons/tooltip.svg" width="21" height="21"></p>
                        <b-tooltip target="tooltip-target" triggers="hover">
                            <div class="p-3">
                                <div class="d-flex align-items-center">
                                    <img src="@/assets/images/icons/time-icon.svg" width="21" height="21" />
                                    <div style="font-size: 1.1rem" class="ml-2">What's this?</div>
                                </div>
                                <div class="d-flex justify-content-start text-left mt-2">
                                    This timer indicates how long your session will remain active to assign the device. If it expires, you'll be redirected to the dashboard, and the assignment process will need to be repeated
                                </div>
                            </div>
                        </b-tooltip>
                    </div>
                    <button @click="cancelTest()" class="btn btn-danger btn-cancel-test btn-block-mobile btn-lg lg-shadow ml-2 mt-4 mt-md-0">Cancel test</button>
                </div>
            </div>

            <PatientDetails
                v-if="Object.keys(patientDetails).length"
                :patient="patientDetails"
                :gdEstimatedDeliveryDate="eddDate"
                :hideEdit="Boolean(true)"
                :hideView="Boolean(true)"
                class="mt-4" 
            />

            <div class="row mt-4">
                <div class="col">
                    <div class="card text-sm">
                        <div class="card-body">
                            <h5 class="mb-0">Test details</h5>
                            <div class="row mt-4">
                                <div class="col-lg-6 pr-lg-6">
                                    <div v-if="testDates && testDates.length">
                                        <div v-for="(testDate, index) in testDatesComputed" :key="index" class="row">
                                            <div class="col-sm-3 font-weight-medium">Test type</div>
                                            <div class="col-sm-9">{{ (testDate.name == '16_week' ? '16 week test' : testDate.name == '26_week' ? '26 week test' : 'Other test') }} : {{ testDate.date | moment("DD/MM/YYYY") }}</div>
                                        </div>
                                    </div>

                                    <div v-if="dueDate" class="row">
                                        <div class="col-sm-3 font-weight-medium">Test date</div>
                                        <div class="col-sm-9">{{ dueDate | moment("DD/MM/YYYY") }}</div>
                                    </div>

                                    <hr>

                                    <div class="row">
                                        <div class="col-sm-3 font-weight-medium">Facility name</div>
                                        <div class="col-sm-9">{{ facilityComputed.name }}</div>
                                    </div>

                                    <hr>

                                    <div class="row">
                                        <div class="col-sm-3 font-weight-medium">Assigned method</div>
                                        <div v-if="method == IssueMethod.IN_PERSON" class="col-sm-9">In person</div>
                                        <div v-if="method == IssueMethod.POST" class="col-sm-9">Delivery to patient</div>
                                        <div v-if="method == IssueMethod.COLLECTION" class="col-sm-9">Collection</div>
                                    </div>

                                    <div v-if="orderingOnBehalfOfOtherHcp == 1 && orderingOnBehalfOfOtherHcpData.name !== null">
                                        <hr>

                                        <div class="row">
                                            <div class="col-sm-3 font-weight-medium">Assigned HCP</div>
                                            <div class="col-sm-9">{{ orderingOnBehalfOfOtherHcpData.name }}</div>
                                        </div>

                                        <hr />

                                        <div class="row">
                                        <div class="col-sm-3 font-weight-medium">Created by</div>
                                            <div class="col-sm-9">{{ formatTitle(userTitle) }} {{ userPractitionerName }}</div>
                                        </div>
                                    </div>

                                    <div v-else>
                                        <hr>

                                        <div class="row">
                                            <div class="col-sm-3 font-weight-medium">Assigned HCP</div>
                                            <div class="col-sm-9">{{ formatTitle(userTitle) }} {{ userPractitionerName }}</div>
                                        </div>
                                    </div>

                                    <div v-if="isTrial">
                                        <hr>
                                        <div class="row">
                                            <div class="col-sm-3 font-weight-medium">Date assigned</div>
                                            <div class="col-sm-9">{{ formatDateGeneral(new Date()) }}</div>
                                        </div>
                                    </div>

                                    <div v-if="!isTrial">
                                        <hr>

                                        <div class="row">
                                            <div class="col-sm-3 font-weight-medium">Date assigned</div>
                                            <div class="col-sm-9">N/A</div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-lg-6 pl-lg-6">
                                    <div class="row">
                                        <div class="col-sm-3 font-weight-medium">Testing for</div>
                                        <div v-if="reason == TestIssueReason.GD" class="col-sm-9">Gestational Diabetes Mellitus</div>
                                        <div v-if="reason == TestIssueReason.CF" class="col-sm-9">Cystic Fibrosis</div>
                                        <div v-if="reason == TestIssueReason.TYPE1" class="col-sm-9">Type 1</div>
                                        <div v-if="reason == TestIssueReason.TYPE2" class="col-sm-9">Type 2</div>
                                        <div v-if="reason == TestIssueReason.RESEARCH_STUDY" class="col-sm-9">Research Study</div>
                                    </div>

                                    <hr>

                                    <div class="row">
                                        <div class="col-sm-3 font-weight-medium">Test created</div>
                                        <div class="col-sm-9">{{ formatDateGeneral(new Date())}}</div>
                                    </div>

                                    <hr>

                                    <div class="row">
                                        <div class="col-sm-3 font-weight-medium">Delivery address</div>
                                        <div class="col-sm-9">{{ method == IssueMethod.IN_PERSON ? 'N/A' : formatAddress(deliveryAddress) }}</div>
                                    </div>

                                    <hr>

                                    <div class="row">
                                        <div class="col-sm-3 font-weight-medium">Leaflet and communications language</div>
                                        <div class="col-sm-9">{{ formatLanguageLabel(this.language) }}</div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="tokenResponse != null && tokenResponse.deviceSerialNumber" class="row mt-5">
                                <div class="col-lg-6 pr-lg-6">
                                    <div class="font-weight-medium">Device details</div>
                                    <div class="alert alert-secondary mt-3">
                                        <div class="row">
                                            <div class="col-sm-3 font-weight-medium">Device serial number</div>
                                            <div class="col-sm-9">{{ tokenResponse.deviceSerialNumber }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="testReasons && testReasons.length" class="mt-4">
                                <hr />
                                <h5 class="mb-0 mt-4">Risk factors</h5>
                                <ul class="list-unstyled with-ticks mt-4" style="column-count: 3">
                                    <li v-for="(reason, index) in testReasons" :key="index" class="mb-3">{{ getReasonLabel(reason) }}</li>
                                </ul>
                            </div>

                            <div v-if="ccEmails != null && ccEmails.length > 0" class="mt-4">
                                <hr />
                                <h5 class="mb-0 mt-4">Notify teams</h5>
                                <ul class="list-unstyled with-ticks mt-4" style="column-count: 3">
                                    <li v-for="ccEmail in ccEmails" :key="ccEmail" class="mb-3">{{ getTeamLabel(ccEmail) }}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="note" class="row mt-4">
                <div class="col">
                    <div class="card pb-25">
                        <div class="card-body">
                            <h5 class="mb-0">Notes</h5>

                            <div class="note mt-4">
                                <div class="note-header d-flex justify-content-between align-items-start align-items-md-center">
                                    <div><p class="mb-0 creator">{{ formatTitle(userTitle) }} {{ userPractitionerName }}</p></div>
                                    <div><p class="mb-0 text-sm"><span class="font-weight-bold font-italic mr-4">Test note</span> Just Now</p></div>
                                </div>

                                <div class="note-body text-sm">
                                    {{ note }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="fixed-bottom bg-white py-25">
            <div class="container-fluid">
                <div class="row">
                    <div class="col text-right center-mobile">
                        <a @click="$router.go(-1)" :disabled="loading" class="btn btn-cancel btn-lg px-5 lg-shadow" :class="{disabled : loading}">Back</a>
                        <button :disabled="loading" @click="confirmTest()" class="btn btn-success btn-lg px-5 px-md-6 lg-shadow ml-2" :class="{disabled : loading}">Confirm</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import {mapGetters, mapActions} from 'vuex'
import Loader from '@/partials/Loader'
import Breadcrumbs from '@/partials/site-structure/BreadcrumbsComponent'
import PatientDetails from '../../../Common/components/PatientDetailsComponent'
import FormatterMixin from '../../../Common/mixins/FormatterMixin'
import CancelTestModalComponent from '@/components/modals/CancelTestModalComponent'
import IssueMethod from '../../../Common/enums/IssueMethod'
import TestIssueReason from '../../../Common/enums/TestIssueReason'
import SignalRMixin from '@/mixins/SignalRMixin'
import {confirmTestFailureMessage} from '@/helpers/confirmTestMessages'
import SessionExpiredModal from '@/components/modals/SessionExpiredModalComponent'

export default {
    name: 'Confirmation',

    mixins: [
        FormatterMixin,
        SignalRMixin
    ],

    components: {
        Breadcrumbs,
        Loader,
        PatientDetails
    },

    data() {
        return {
            loading: true,
            CancelTestModalComponent,
            IssueMethod,
            TestIssueReason,
            SessionExpiredModal
        }
    },

    async mounted() {
        if(!this.patientName && !this.facilityId && !this.patientId && !this.patientTitle && !this.dueDate && !this.reason && !this.deviceId) {
            return this.$router.push({name: 'Dashboard'})
        }

        this.getTokenExpiry()

        try {
            await this.getPatientDetails()
        }
        catch {
            this.setNotification({
                type: 'error',
                title: 'Error',
                message: 'Sorry, the patient details were not fetched'
            })
        }
        finally {
            this.loading = false
        }
    },

    watch: {
        tokenExpirySeconds: function(val) {
            if(val != null && val <= 0) {
                this.$modal.show(SessionExpiredModal, {}, {width: '400px'})
                return this.$router.push({name: 'TestDetails'})
            } 
        }
    },

    computed: {
        ...mapGetters({
            languages: 'LanguageStore/GetLanguages',
            organisationSettingsMap: 'OrganisationStore/GetOrganisationSettingsMap',
            myOrganisationSettings: 'OrganisationStore/GetMyOrganisationSettings',
            isTrial: 'OrganisationStore/GetIsTrial',
            myFacilities: 'FacilityStore/GetMyFacilities',
            organisationId: 'UserStore/GetUserOrganisationId',
            facilityId: 'TestStore/GetFacilityId',
            userFullName: 'UserStore/GetUserFullName',
            userTitle: 'UserStore/GetUserTitle',
            userPractitionerName: 'UserStore/GetUserFullName',
            practitionerId: 'UserStore/GetUserPractitionerId',
            patientId: 'TestStore/GetPatientId',
            patientName: 'TestStore/GetPatientName',
            patientTitle: 'TestStore/GetPatientTitle',
            deviceId: 'TestStore/GetDeviceId',
            testDates: 'TestStore/GetTestDatesResponse',
            dueDate: 'TestStore/GetDueDate',
            eddDate : 'TestStore/GetEddDate',
            reason: 'TestStore/GetReason',
            method: 'TestStore/GetMethod',
            note: 'TestStore/GetNote',
            collectionPoint: 'TestStore/GetCollectionPoint',
            deliveryAddress: 'TestStore/GetDeliveryAddress',
            shippingAddressType: 'TestStore/GetShippingAddressType',
            language: 'TestStore/GetLanguage',
            testReasons: 'TestStore/GetTestReasons',
            ccEmails: 'TestStore/GetTestTeamsCCEmails',
            orderingOnBehalfOfOtherHcp: 'TestStore/GetOrderingOnBehalfOfOtherHcp',
            orderingOnBehalfOfOtherHcpData: 'TestStore/GetOrderingOnBehalfOfOtherHcpData'
        }),

        testDatesComputed() {
            if(this.testDates != null && this.testDates.length) {
                return this.testDates.filter(x => x.active && x.date != null)
            }

            return []
        },

        facilityComputed() {
            let fac = this.myFacilities.find(x => x.id == this.facilityId)
            return fac
        }
    },  

    methods: {
        ...mapActions({
            setNotification: 'NotificationStore/SetNotification',
            clearTestStoreData: 'TestStore/ClearData',
            clearSearchStore: 'SearchStore/ClearSearchStore'
        }),

        async getPatientDetails() {
            let response = await axios.get(`${process.env.VUE_APP_AXIOS_PATIENT_BASE_URL}/api/v1/patients/${this.organisationId}/${this.patientId}`)

            this.patientDetails = response.data
        },

        async confirmTest() {
            this.loading = true

            var testDates = this.testDatesComputed
            if(testDates.length) {
                testDates = testDates.map(x => x.date)
            }
            
            let testModel = {
                organisationId: this.organisationId,
                facilityId: this.facilityId,
                practitionerId: this.practitionerId,
                patientId: this.patientId,
                deviceNumber: this.deviceId,
                deviceRawData: null,
                collectionPointId: this.collectionPoint,
                address: this.deliveryAddress,
                shippingAddressType: this.shippingAddressType,
                dueDate: this.dueDate,
                dueDates: testDates,
                gDEstimatedDeliveryDate : this.eddDate,
                reason: this.reason,
                method: this.method,
                notes: this.note,
                language: this.language,
                testReasons: this.testReasons,
                ccEmails: this.ccEmails,
                hcpAssignmentToken: this.token
            }

            if(this.orderingOnBehalfOfOtherHcp == 1 && this.orderingOnBehalfOfOtherHcpData.id != null && this.orderingOnBehalfOfOtherHcpData.name != null) {
                testModel.practitionerId = this.orderingOnBehalfOfOtherHcpData.id
                testModel.createdByPractitionerId = this.practitionerId
            }

            try {
                const patientName = this.patientName
                await axios.post(`${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/api/v1/tests`, testModel)
                this.clearTestStoreData()
                this.clearSearchStore()
                return this.$router.push({name: 'DeviceAssigned', params: { patientName: patientName }})
            }
            catch(err) {
                this.setNotification({
                    type: 'error',
                    title: 'Error',
                    message: confirmTestFailureMessage(this.token)
                })
            }
            finally {
                this.loading = false
            }
        },

        cancelTest() {
            this.$modal.show(CancelTestModalComponent, {}, {width: '400px'});
        },

        getReasonLabel(reason) {
            var found = this.organisationSettingsMap.find(x => x.key == reason)
            if(found == null) return reason
            return found.label
        },

        getTeamLabel(email) {
            if (this.myOrganisationSettings.ccEmails != null && email != null){
                let label = "";
                Object.entries(this.myOrganisationSettings.ccEmails).map(
                    ([key, value]) => {
                        if (value === email){
                            label = key;
                        }
                    }
                )
                return label
            }
            return email
        },

        beforeDestroy() {
            this.stopTokenExpiryCountdown()
        }
    }
}
</script>